/**
 * @fileoverview The Founding Ensemble medal — the Hans mark inlaid in struck gold.
 *
 * A founding member's badge should look awarded, not decorated. The earlier
 * version was one radial gradient and a ring shadow, which reads as
 * *gold-coloured* rather than as gold: a single highlight is how plastic
 * behaves. Metal is lit from every direction at once, so the disc is built in
 * four layers, and each one is doing a job:
 *
 * 1. **`SHEEN` — a conic gradient.** This is the whole difference. Bright arcs
 *    at several angles are what the eye reads as a reflective surface; no
 *    number of radial stops produces it, because a radial gradient can only
 *    have one bright point.
 * 2. **`RIM` — the raised edge**, a second conic offset from the first so the
 *    rim and the face catch the light at different angles, plus an inset
 *    highlight on top and a shadow underneath to give it thickness.
 * 3. **`FIELD` — the recessed face**, lit from the upper left but shadowed
 *    *inside* its top edge. Inverting the lighting against the rim is what
 *    makes the face read as struck below the surface rather than printed on it.
 * 4. **The specular sweep**, a soft diagonal wipe over the face only.
 *
 * **The mark's onyx is baked into `public/founding-mark.png`, not applied
 * here.** The asset is the two Hans waves as a bare silhouette — no centre line
 * and no dot — painted with a vertical onyx ramp, and regenerated by
 * `scripts/make-founding-mark.py` rather than edited by hand. Baking the metal
 * removes a whole class of drift: a CSS filter cannot rotate hue away from
 * white, so a white asset tinted per platform would land neutral-grey on web
 * and cool on iOS. The two medals are the same metal because they are the same
 * pixels.
 *
 * It was light silver first, and dark is the correction: against gold a light
 * inlay has almost nothing to push against, and at 28px the wave dissolved into
 * the field. Onyx still reads at that size.
 *
 * Note this is why mobile must NOT set `tintColor` on the image — that would
 * flatten the baked ramp back to one colour.
 *
 * **A CSS `mask` was tried and abandoned.** It failed *silently*: every
 * longhand computed correctly, the PNG decoded at 240×240, and the medal still
 * rendered as a blank gold disc. A plain `<img>` has no such failure mode, and
 * it is what mobile does, so the two match in approach rather than in output
 * alone. If the image fails to load, what remains is a gold disc — still a
 * medal, just a blank one. That is the right failure.
 */

/** Struck gold. The angles are uneven on purpose — evenly spaced highlights
 *  read as a machined pattern rather than as a reflection. */
const SHEEN = `conic-gradient(from 212deg,
  #7f5303 0deg, #c9931a 30deg, #f7dc78 62deg, #fffaea 80deg, #f2ca42 100deg,
  #b17c0c 138deg, #6f4702 182deg, #a97208 214deg, #e8b92a 252deg,
  #fff6d2 284deg, #dfa716 312deg, #9a6706 340deg, #7f5303 360deg)`;

/** The raised edge, offset from `SHEEN` so rim and face never flash together. */
const RIM = `conic-gradient(from 212deg,
  #9c6a05 0deg, #f0cd55 55deg, #fffdf3 92deg, #e2ac1c 140deg, #8c5d04 196deg,
  #c9931a 262deg, #fff4c8 300deg, #b8830f 336deg, #9c6a05 360deg)`;

/** The struck face: lit upper-left, and shadowed inside its own top edge. */
const FIELD = "radial-gradient(circle at 33% 25%, #fbe487 0%, #eab92a 34%, #bd860e 70%, #7c5304 100%)";

/** A wipe of light across the face, with a whisper of bounce at the far edge. */
const SPECULAR = `linear-gradient(148deg,
  rgba(255,255,255,0.40) 0%, rgba(255,255,255,0.13) 24%,
  rgba(255,255,255,0) 44%, rgba(255,255,255,0) 74%, rgba(255,255,255,0.07) 100%)`;

/** Relief on the inlay, and it INVERTS with the metal. A light mark needed a
 *  dark edge to stop it floating on light gold; a dark one is already separated
 *  from the field, so what it wants is a lit lower edge — the gold catching
 *  light beneath a struck-in shape — and only a whisper of shadow above. */
const INLAY_RELIEF =
  "drop-shadow(0 1px 0.5px rgba(255,250,220,0.55)) drop-shadow(0 -1px 1px rgba(40,26,0,0.45))";

export default function FoundingMedal({ size = 48, className = "", title = "Founding Ensemble" }) {
  // Every inset scales with the disc, so one component serves the 28px badge on
  // a settings row and the 200px hero without the rim thickening at small sizes.
  const px = (n) => `${(size * n).toFixed(2)}px`;

  return (
    <div
      role="img"
      aria-label={title}
      data-testid="founding-medal"
      className={`relative shrink-0 rounded-full ${className}`}
      style={{
        width: size,
        height: size,
        background: SHEEN,
        boxShadow: `0 ${px(0.09)} ${px(0.24)} rgba(214,160,20,0.34), 0 ${px(0.02)} ${px(0.06)} rgba(0,0,0,0.55)`,
      }}
    >
      <div
        className="absolute inset-0 rounded-full"
        style={{
          background: RIM,
          boxShadow: "inset 0 1px 1.5px rgba(255,255,255,0.8), inset 0 -1px 2px rgba(86,56,0,0.75)",
        }}
      />
      <div
        className="absolute rounded-full"
        style={{
          inset: px(0.075),
          background: FIELD,
          boxShadow: `inset 0 ${px(0.02)} ${px(0.045)} rgba(70,45,0,0.6), inset 0 -1px 1px rgba(255,248,214,0.5)`,
        }}
      />
      {/* The struck line just inside the rim. Hidden below ~40px, where it stops
          being a detail and becomes noise on the edge. */}
      {size >= 40 && (
        <div
          className="absolute rounded-full"
          style={{
            inset: px(0.115),
            boxShadow: "inset 0 0 0 1px rgba(120,80,4,0.34), 0 0 0 1px rgba(255,244,200,0.2)",
          }}
        />
      )}
      <img
        src="/founding-mark.png"
        alt=""
        aria-hidden="true"
        className="absolute inset-0 h-full w-full"
        style={{ padding: px(0.225), boxSizing: "border-box", filter: INLAY_RELIEF }}
      />
      {/* Last, and over the face only — a highlight that crossed the rim would
          flatten the very edge the rim exists to describe. */}
      <div
        className="pointer-events-none absolute rounded-full"
        style={{ inset: px(0.075), background: SPECULAR }}
      />
    </div>
  );
}
